import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { dreAssumptionDFCResponse } from '@interfaces/dre/response/dreAssumptionDFCResponse';
import { PdfUtilsServiceV2 } from '@src/app/core/services/helpers/pdf-utils/pdf-utils-v2.service';
import { TableAbstractComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/table-abstract/table-abstract.component';
import { TableExportService } from '@src/app/shared/components/table/services/table-export.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { DfpPanelComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/dfp-panel/dfp-panel.component';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { DfpColumnWidthDirective } from '@app/features/dashboard/performance/pages/dfp/components/table-abstract/dfp-column-width.directive';
import { DfpClipGuardDirective } from '@app/features/dashboard/performance/pages/dfp/components/table-abstract/dfp-clip-guard.directive';
import {
  DfpCalculationColumn,
  DfpCalculationInfoComponent,
} from '@src/app/features/dashboard/performance/pages/dfp/components/calculation-info/calculation-info.component';

/** Uma linha da DFC com o valor de cada ano. */
interface CashFlowRow {
  name: string;
  groupPrefix: string;
  subGroupPrefix: string;
  isTotal: boolean;
  values: Map<number, number>;
  /** Linhas do backend desta conta (uma por ano), com a memória de cálculo quando houver. */
  lines: dreAssumptionDFCResponse[];
}

/**
 * Nome exibido das linhas (crítica de design, G8): o motor da modelagem usa rótulos internos. "Linha de Verificação" é o
 * caixa do Balanço menos o Caixa Final calculado, e "Caixa Ajustado" é o caixa do próprio Balanço; com os nomes abaixo a
 * pessoa entende por que os dois saldos podem diferir. O backend usa os mesmos nomes nas parcelas da memória de cálculo
 * (`DfcCalculationMemory.LineDisplayNames`): mudou um, mude o outro.
 */
const DISPLAY_NAMES: Readonly<Record<string, string>> = {
  'Working Capital': 'Capital de giro',
  'Imobilizado BOP': 'Imobilizado no início do ano',
  'Imobilizado EOP': 'Imobilizado no fim do ano',
  'Variação Cx': 'Variação de caixa',
  'Caixa Final': 'Caixa final calculado',
  'Linha de Verificação': 'Diferença para o caixa do Balanço',
  'Caixa Ajustado': 'Caixa no Balanço',
};

/** Linhas de total e de saldo da DFC (destaque na tabela). */
const TOTAL_ROW_PATTERN = /^(fluxo de caixa|caixa|varia)/i;

/**
 * DFC (Demonstração do Fluxo de Caixa, método indireto) dos anos realizados, na aba Demonstrativos da DFP Histórica
 * (PBI 3875). Os valores vêm prontos do backend (`dfp-historical/dfc`, mesmo cálculo da modelagem); aqui só agrupamos
 * as linhas por conta e ano, na ordem de grupo e subgrupo que o backend define.
 */
@Component({
  selector: 'app-table-cash-flow',
  templateUrl: './table-cash-flow.component.html',
  styleUrls: ['../table-abstract/table-abstract.component.scss'],
  imports: [
    CommonModule,
    SharedModule,
    TableModule,
    MenuModule,
    DfpPanelComponent,
    DfpColumnWidthDirective,
    DfpClipGuardDirective,
    DfpCalculationInfoComponent,
  ],
})
export class TableCashFlowComponent extends TableAbstractComponent {
  @Input() companyName: string = '';
  override tableData: dreAssumptionDFCResponse[] = [];
  rows: CashFlowRow[] = [];

  constructor(pdfUtilsService: PdfUtilsServiceV2, tableExportService: TableExportService) {
    super(pdfUtilsService, tableExportService);
  }

  override groupTableData(): void {
    const byKey = new Map<string, CashFlowRow>();
    for (const line of this.tableData) {
      const key = `${line.groupPrefix}|${line.subGroupPrefix}|${line.name}`;
      let row = byKey.get(key);
      if (!row) {
        row = {
          name: DISPLAY_NAMES[line.name] ?? line.name,
          groupPrefix: line.groupPrefix,
          subGroupPrefix: line.subGroupPrefix,
          isTotal: TOTAL_ROW_PATTERN.test(line.name ?? ''),
          values: new Map<number, number>(),
          lines: [],
        };
        byKey.set(key, row);
      }
      row.values.set(line.year, line.value);
      row.lines.push(line);
    }

    this.rows = [...byKey.values()].sort(
      (a, b) =>
        Number(a.groupPrefix) - Number(b.groupPrefix) ||
        Number(a.subGroupPrefix) - Number(b.subGroupPrefix)
    );
  }

  /**
   * Colunas do balão de memória de cálculo de uma linha da DFC (PBI 3875): os anos em que o backend mandou `calculation`.
   * A fórmula e as parcelas vêm prontas; aqui só se casa linha com coluna e se formata o valor como a célula mostra.
   */
  calculationColumnsFor(row: CashFlowRow): DfpCalculationColumn[] {
    return this.calculationColumnsFrom(row.lines, line => this.formatValue(line.value));
  }

  override exportToPdf(): void {
    this.exportPerformanceTableToPdf({
      filename: 'demonstrativo-fluxo-caixa.pdf',
      title: 'Demonstração do Fluxo de Caixa',
      subtitle: this.companyName,
    });
  }

  override exportToExcel(): void {
    this.exportPerformanceTableToExcel({
      selector: '.performance-table--cash-flow',
      sheetName: 'DFC',
      filenamePrefix: 'demonstrativo-fluxo-caixa',
    });
  }
}
